// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

import { captureMessage } from "@sentry/react";
import packageJson from "../package.json";

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL;

// Set this to the current package.json version to show the update modal on this deploy.
// Set it to any older version for silent background updates (no modal, no forced reload).
// Important: This way of updating the service worker is deprecated. Only way forward is to use the backend to manage the service worker.
const FORCE_UPDATE_VERSION = "1.5.12";

function compareSemver(v1, v2) {
  const toNums = (v) => v.split(".").map(Number);
  const [a1, b1, c1] = toNums(v1);
  const [a2, b2, c2] = toNums(v2);
  return a1 !== a2 ? a1 - a2 : b1 !== b2 ? b1 - b2 : c1 - c2;
}

function shouldShowForceUpdate() {
  return compareSemver(FORCE_UPDATE_VERSION, packageJson.version) >= 0;
}

export function register(config) {
  if ("serviceWorker" in navigator) {
    const publicUrl = new URL(PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `/sw.js`;
      registerValidSW(swUrl, config);
    });
  } else {
    captureMessage("ServiceWorker API not available in navigator", {
      level: "warning",
      tags: { feature: "service-worker" },
      extra: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        isReactNativeWebView: !!window.ReactNativeWebView,
      },
    });
  }
}

function showUpdateModal(registration) {
  window.__SW_REGISTRATION = registration;
  const div = document.getElementById("service-worker-modal-root");
  if (div) {
    div.style.display = "block";
  }
}

function handleUpdateReady(registration, config) {
  if (shouldShowForceUpdate()) {
    showUpdateModal(registration);
    if (config && config.onUpdate) {
      config.onUpdate(registration);
    }
  } else {
    // Silent update: activate new SW immediately in the background.
    // controllerchange fires but userTriggeredUpdate is false, so no reload.
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
}

function registerValidSW(swUrl, config) {
  // Only reload on controllerchange when user explicitly triggered the update.
  // Without this guard, the first install also fires controllerchange
  // (via clientsClaim) and causes an unwanted reload / white screen.
  let userTriggeredUpdate = false;
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!userTriggeredUpdate || refreshing) return;
    refreshing = true;
    window.location.reload();
  });
  window.__SW_TRIGGER_RELOAD = () => {
    userTriggeredUpdate = true;
  };

  navigator.serviceWorker
    .register(swUrl, { scope: "/" })
    .then((registration) => {
      // If there's already a waiting worker from a previous visit, handle update
      if (registration.waiting) {
        handleUpdateReady(registration, config);
      }

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // New content available — show modal or apply silently based on FORCE_UPDATE_VERSION
              handleUpdateReady(registration, config);
            } else {
              // First install — content cached for offline use
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error("Error during service worker registration:", error);
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
